import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Paperclip, Download, FileText, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

/**
 * BuktiFilesPreview
 * Chip "📎 N lampiran" yang klik → dialog dengan preview gambar/PDF.
 * Props:
 *   files: [{ id, filename, content_type, size }]
 *   testid?: string
 */
export default function BuktiFilesPreview({ files, testid }) {
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState({}); // { fileId: blobUrl }
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Clean up blob URLs when dialog closes
    if (!open) {
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
      setUrls({});
    }
    // eslint-disable-next-line
  }, [open]);

  const loadPreviews = async () => {
    setLoading(true);
    const map = {};
    try {
      await Promise.all(
        (files || []).map(async (f) => {
          try {
            const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: res.headers["content-type"] || f.content_type });
            map[f.id] = URL.createObjectURL(blob);
          } catch (e) {
            // skip
          }
        }),
      );
      setUrls(map);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && files?.length && Object.keys(urls).length === 0) {
      loadPreviews();
    }
    // eslint-disable-next-line
  }, [open]);

  const downloadOne = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || f.content_type });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = f.filename || f.id;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!files || files.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[11px] font-semibold" data-testid={testid ? `${testid}-empty` : undefined}>
        <X className="h-3 w-3" /> Tanpa lampiran
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 px-2 py-0.5 text-[11px] font-semibold"
        data-testid={testid || "btn-bukti-open"}
      >
        <Paperclip className="h-3 w-3" /> {files.length} lampiran
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bukti-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Paperclip className="h-5 w-5" /> Lampiran Bukti PPDP ({files.length})
            </DialogTitle>
          </DialogHeader>
          {loading && <div className="text-sm text-slate-500 py-4 text-center">Memuat lampiran...</div>}
          <div className="space-y-3">
            {files.map((f) => {
              const isImg = (f.content_type || "").startsWith("image/");
              const isPdf = (f.content_type || "").includes("pdf");
              const url = urls[f.id];
              return (
                <div key={f.id} className="border border-slate-200 rounded-md p-3" data-testid={`bukti-file-${f.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {isImg ? <ImageIcon className="h-4 w-4 text-slate-500 shrink-0" /> : <FileText className="h-4 w-4 text-slate-500 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{f.filename}</div>
                        <div className="text-xs text-slate-500">
                          {f.content_type} · {f.size ? `${(f.size / 1024).toFixed(1)} KB` : "-"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadOne(f)}
                      className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-1 shrink-0"
                      data-testid={`btn-download-bukti-${f.id}`}
                    >
                      <Download className="h-3 w-3" /> Download
                    </button>
                  </div>
                  {url && isImg && (
                    <img src={url} alt={f.filename} className="max-w-full max-h-[500px] mx-auto rounded border border-slate-100" />
                  )}
                  {url && isPdf && (
                    <iframe src={url} title={f.filename} className="w-full h-[500px] rounded border border-slate-100" />
                  )}
                  {!url && !loading && (
                    <div className="text-xs text-slate-500 italic py-3 text-center">Preview tidak tersedia</div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
